import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useI18n } from '../contexts/I18nContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Menu, X, Globe, User, Bell, Store, ExternalLink } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import api from '../utils/api';

const Navbar = () => {
  const { t, lang, setLang, LANGS } = useI18n();
  const { user, logout } = useAuth();
  const { cartItems } = useCart();
  const { newOrderCount, clearNewOrderCount, permission, requestPermission, pendingShopCount } = useNotifications();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shopSlug, setShopSlug] = useState(null);

  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Fetch user's shop slug if logged in
  useEffect(() => {
    if (user) {
      api.get('/reseller/shop').then(res => {
        if (res.data.exists && res.data.shop?.shop_slug) {
          setShopSlug(res.data.shop.shop_slug);
        }
      }).catch(() => {});
    }
  }, [user]);

  const handleAdminClick = () => {
    clearNewOrderCount();
    navigate('/admin');
  };

  const handleShopClick = () => {
    // Immer in den Verwaltungsbereich – öffentlicher Shop ist von dort per "Shop ansehen"-Button erreichbar
    navigate('/my-shop');
  };

  return (
    <header className="sticky top-0 z-50 bg-gray-800/80 backdrop-blur-sm">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 md:h-16 items-center justify-between">

          {/* LOGO = immer Home */}
          <Link to="/" className="flex items-center gap-3">
            {/* Logo Container weiß hinterlegt */}
            <div className="bg-white rounded-md px-2 py-1 shadow-md flex items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_barber-supply/artifacts/m8xbva3r_file_000000005a2c71f490cada719be81da8.png"
                alt="ED STEEL"
                className="h-8 md:h-9 w-auto object-contain"
                draggable="false"
              />
            </div>

            {/* Text daneben */}
            <span className="text-white font-extrabold tracking-widest text-sm md:text-base">
              ED STEEL
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-4 text-white text-sm md:text-base font-medium" data-testid="desktop-navigation">
            <Link to="/" className="hover:underline" data-testid="desktop-home-link">
              {t('home')}
            </Link>
            <Link to="/shop" className="hover:underline" data-testid="desktop-shop-link">
              {t('shop')}
            </Link>
            <Link to="/favorites" className="hover:underline" data-testid="desktop-favorites-link">
              {t('favorites')}
            </Link>
            <Link to="/cart" className="relative hover:underline" data-testid="desktop-cart-link">
              {t('cart')}
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -end-3 bg-gold text-primary text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </Link>
            
            {/* Mein Shop – prominent im Nav für alle eingeloggten B2B-Händler */}
            {user && (
              <button
                onClick={handleShopClick}
                className="flex items-center gap-1 text-white hover:underline"
                data-testid="desktop-my-shop-nav"
                title={shopSlug ? 'Shop verwalten' : 'Endkundenshop einrichten'}
              >
                <Store className="h-4 w-4" />
                <span className="hidden lg:inline">{shopSlug ? 'Mein Shop' : 'Shop einrichten'}</span>
              </button>
            )}
            
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-white hover:underline" data-testid="language-selector">
                  <Globe className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {LANGS.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`cursor-pointer ${lang === l.code ? 'bg-gray-100' : ''}`}
                    data-testid={`lang-${l.code}`}
                  >
                    <span className="me-2">{l.flag}</span>
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Admin Notification Bell */}
            {user?.role === 'admin' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    onClick={() => {
                      if (permission === 'default') {
                        requestPermission();
                      }
                    }}
                    className="relative text-white hover:text-gold transition-colors"
                    data-testid="admin-notification-bell"
                    title="Benachrichtigungen"
                  >
                    <Bell className={`h-5 w-5 ${pendingShopCount > 0 ? 'text-amber-400' : permission === 'granted' ? 'text-green-400' : ''}`} />
                    {(newOrderCount + pendingShopCount) > 0 && (
                      <span className="absolute -top-2 -end-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse" data-testid="notification-badge">
                        {newOrderCount + pendingShopCount}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  {pendingShopCount > 0 && (
                    <DropdownMenuItem
                      onClick={() => navigate('/admin?tab=reseller-shops')}
                      className="cursor-pointer"
                      data-testid="notification-pending-shops"
                    >
                      <Store className="h-4 w-4 me-2 text-amber-600" />
                      <div>
                        <div className="font-semibold text-amber-700">
                          {pendingShopCount} {pendingShopCount === 1 ? 'Händler wartet' : 'Händler warten'} auf Freigabe
                        </div>
                        <div className="text-xs text-gray-500">Jetzt B2C-Shop aktivieren</div>
                      </div>
                    </DropdownMenuItem>
                  )}
                  {newOrderCount > 0 && (
                    <DropdownMenuItem
                      onClick={() => { clearNewOrderCount(); navigate('/admin?tab=orders'); }}
                      className="cursor-pointer"
                      data-testid="notification-new-orders"
                    >
                      <Bell className="h-4 w-4 me-2 text-green-600" />
                      <div>
                        <div className="font-semibold">{newOrderCount} neue {newOrderCount === 1 ? 'Bestellung' : 'Bestellungen'}</div>
                        <div className="text-xs text-gray-500">Zu den B2B-Bestellungen</div>
                      </div>
                    </DropdownMenuItem>
                  )}
                  {pendingShopCount === 0 && newOrderCount === 0 && (
                    <DropdownMenuItem disabled data-testid="notification-empty">
                      {t('noNewNotifications')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-white hover:underline" data-testid="desktop-profile-menu-button">
                  <User className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="desktop-profile-link">
                  {t('profile')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/orders')} data-testid="desktop-orders-link">
                  {t('orders')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleShopClick} data-testid="desktop-my-shop-link">
                  <Store className="h-4 w-4 me-2" />
                  {t('manageShop')}
                </DropdownMenuItem>
                {shopSlug && (
                  <DropdownMenuItem onClick={() => window.open(`/shop/${shopSlug}`, '_blank')} data-testid="desktop-open-public-shop-link">
                    <ExternalLink className="h-4 w-4 me-2" />
                    {t('viewPublicShop')}
                  </DropdownMenuItem>
                )}
                {user?.role === 'admin' && (
                  <DropdownMenuItem onClick={handleAdminClick} className="relative" data-testid="desktop-admin-link">
                    {t('admin')}
                    {newOrderCount > 0 && (
                      <span className="ms-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                        {newOrderCount}
                      </span>
                    )}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} data-testid="desktop-logout-button">
                  {t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-primary-foreground/20 py-4">
            <nav className="flex flex-col gap-3 text-white" data-testid="mobile-navigation">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="hover:underline" data-testid="mobile-home-link">
                {t('home')}
              </Link>
              <Link to="/shop" onClick={() => setMobileMenuOpen(false)} className="hover:underline" data-testid="mobile-shop-link">
                {t('shop')}
              </Link>
              <Link to="/favorites" onClick={() => setMobileMenuOpen(false)} className="hover:underline" data-testid="mobile-favorites-link">
                {t('favorites')}
              </Link>
              <Link to="/cart" onClick={() => setMobileMenuOpen(false)} className="hover:underline" data-testid="mobile-cart-link">
                {t('cart')} {cartItemCount > 0 && `(${cartItemCount})`}
              </Link>
              <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="hover:underline" data-testid="mobile-profile-link">
                {t('profile')}
              </Link>
              <Link to="/orders" onClick={() => setMobileMenuOpen(false)} className="hover:underline" data-testid="mobile-orders-link">
                {t('orders')}
              </Link>
              <Link to="/my-shop" onClick={() => setMobileMenuOpen(false)} className="hover:underline flex items-center gap-2" data-testid="mobile-my-shop-link">
                <Store className="h-4 w-4" />
                {t('manageShop')}
              </Link>
              {shopSlug && (
                <button 
                  onClick={() => { setMobileMenuOpen(false); window.open(`/shop/${shopSlug}`, '_blank'); }} 
                  className="hover:underline flex items-center gap-2 text-left"
                  data-testid="mobile-open-shop-link"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t('viewPublicShop')}
                </button>
              )}
              {user?.role === 'admin' && (
                <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="hover:underline flex items-center gap-2" data-testid="mobile-admin-link">
                  {t('admin')}
                  {(newOrderCount + pendingShopCount) > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {newOrderCount + pendingShopCount}
                    </span>
                  )}
                </Link>
              )}
              
              {/* Language Selector Mobile */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-primary-foreground/20">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => {
                      setLang(l.code);
                      setMobileMenuOpen(false);
                    }}
                    className={`px-2 py-1 rounded text-sm ${lang === l.code ? 'bg-white text-gray-800' : 'bg-gray-700'}`}
                    data-testid={`mobile-lang-${l.code}`}
                  >
                    {l.flag} {l.label}
                  </button>
                ))}
              </div>
              
              <button onClick={logout} className="text-start hover:underline text-red-300" data-testid="mobile-logout-button">
                {t('logout')}
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

const Layout = () => {
  const { t } = useI18n();
  const { cartItems } = useCart();
  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      {/* Hauptinhalt */}
      <main className="flex-1 pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Desktop Footer */}
      <footer className="hidden md:block bg-gray-900 text-white mt-auto">
        <div className="container mx-auto px-4 lg:px-12 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Logo & Kontakt */}
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_barber-supply/artifacts/m8xbva3r_file_000000005a2c71f490cada719be81da8.png"
                alt="ED STEEL"
                className="h-10 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-gray-400 text-sm">
                {t('b2bTagline')}
              </p>
              <div className="mt-4 text-gray-400 text-sm">
                <p>{t('phone')}: +49 171 629 5816</p>
                <p>E-Mail: ed-steel@outlook.de</p>
              </div>
            </div>

            {/* Shop */}
            <div>
              <h3 className="font-bold mb-4 text-lg">{t('shop')}</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/shop" className="hover:text-white transition-colors">{t('allProducts')}</Link></li>
                <li><Link to="/favorites" className="hover:text-white transition-colors">{t('favorites')}</Link></li>
                <li><Link to="/cart" className="hover:text-white transition-colors">{t('cart')}</Link></li>
              </ul>
            </div>

            {/* Kundenservice */}
            <div>
              <h3 className="font-bold mb-4 text-lg">{t('customerService')}</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/orders" className="hover:text-white transition-colors">{t('orders')}</Link></li>
                <li><Link to="/profile" className="hover:text-white transition-colors">{t('profile')}</Link></li>
                <li><Link to="/versand-zahlung" className="hover:text-white transition-colors">{t('shippingPayment')}</Link></li>
              </ul>
            </div>

            {/* Rechtliches */}
            <div>
              <h3 className="font-bold mb-4 text-lg">{t('legalSection')}</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/impressum" className="hover:text-white transition-colors">{t('imprint')}</Link></li>
                <li><Link to="/agb" className="hover:text-white transition-colors">{t('terms')}</Link></li>
                <li><Link to="/datenschutz" className="hover:text-white transition-colors">{t('privacy')}</Link></li>
                <li><Link to="/widerruf" className="hover:text-white transition-colors">{t('cancellation')}</Link></li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-800 mt-8 pt-6 text-center text-gray-500 text-sm">
            <p>&copy; {new Date().getFullYear()} ED STEEL. {t('allRightsReserved')}</p>
            <p className="mt-1">{t('b2bOnly')}</p>
          </div>
        </div>
      </footer>

      {/* Mobile Fußleiste – NUR auf kleineren Screens */}
      <footer className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
        <div className="flex items-center justify-around h-16 px-2">

          {/* HOME */}
          <Link to="/" className="flex flex-col items-center gap-1">
            <img
              src="https://customer-assets.emergentagent.com/job_barber-supply/artifacts/m8xbva3r_file_000000005a2c71f490cada719be81da8.png"
              alt="ED STEEL"
              className="h-8 w-auto object-contain"
              draggable="false"
            />
          </Link>

          {/* SHOP */}
          <Link to="/shop" className="flex flex-col items-center gap-1">
            <span className="text-lg">🛍️</span>
            <span className="text-xs text-gray-700">{t('shop')}</span>
          </Link>

          {/* WARENKORB */}
          <Link to="/cart" className="flex flex-col items-center gap-1 relative">
            <span className="text-lg">🛒</span>
            <span className="text-xs text-gray-700">{t('cart')}</span>
            {cartItemCount > 0 && (
              <span className="absolute -top-1 -end-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {cartItemCount}
              </span>
            )}
          </Link>

          {/* FAVORITEN */}
          <Link to="/favorites" className="flex flex-col items-center gap-1">
            <span className="text-lg">❤️</span>
            <span className="text-xs text-gray-700">{t('favorites')}</span>
          </Link>

          {/* PROFIL */}
          <Link to="/profile" className="flex flex-col items-center gap-1">
            <span className="text-lg">👤</span>
            <span className="text-xs text-gray-700">{t('profile')}</span>
          </Link>

        </div>
      </footer>
    </div>
  );
};

export default Layout;
